import { ref, onMounted, watch } from 'vue';
import { logger } from '@/infrastructure/services/LoggerService';
import { getApiErrorCode } from '@/infrastructure/services/ApiErrorService';
export function useSearch(searchFn, tableQuery, errorLabel = 'items', options = {}) {
    const { waitFor, ast, refinement } = options;
    const items = ref([]);
    const pageResponse = ref();
    const loading = ref(true);
    const loadError = ref(false);
    const loadErrorCode = ref();
    let loadSeq = 0;
    async function load() {
        const seq = ++loadSeq;
        try {
            loading.value = true;
            loadError.value = false;
            loadErrorCode.value = undefined;
            const inlineAst = ast?.value ?? undefined;
            const response = await searchFn({
                // Mutually exclusive on the wire (BFF 400s `AstAndFilterIdConflict` if both are set) —
                // an applied advanced filter always takes precedence over an active saved view.
                filterId: inlineAst ? null : tableQuery.state.activeViewId,
                ast: inlineAst,
                refinement: refinement?.value ?? undefined,
                search: tableQuery.state.search || undefined,
                page: tableQuery.state.page,
                pageSize: tableQuery.state.pageSize,
                sortBy: tableQuery.state.sort?.column,
                sortOrder: tableQuery.state.sort?.direction,
            });
            if (seq !== loadSeq)
                return;
            items.value = response.result;
            pageResponse.value = {
                currentPage: response.currentPage,
                pageSize: response.pageSize,
                totalItems: response.totalItems,
                totalPages: response.totalPages,
            };
        }
        catch (e) {
            if (seq !== loadSeq)
                return;
            loadError.value = true;
            loadErrorCode.value = getApiErrorCode(e) ?? undefined;
            logger.error(`Failed to load ${errorLabel}`, e);
        }
        finally {
            if (seq === loadSeq)
                loading.value = false;
        }
    }
    if (waitFor) {
        // Defer initial load until filters are ready; include all query state so
        // this single watcher handles both the initial load and subsequent changes.
        // Registering after the view's presetViews watcher ensures the default view
        // is already set when this fires, preventing a double-load with null filterId.
        watch(() => ({
            ready: waitFor.value,
            viewId: tableQuery.state.activeViewId,
            page: tableQuery.state.page,
            pageSize: tableQuery.state.pageSize,
            sort: tableQuery.state.sort,
            search: tableQuery.state.search,
            ast: ast?.value,
            refinement: refinement?.value,
        }), ({ ready }) => {
            if (!ready)
                return;
            load();
        }, { deep: true, immediate: true });
    }
    else {
        onMounted(load);
        watch(() => ({
            viewId: tableQuery.state.activeViewId,
            page: tableQuery.state.page,
            pageSize: tableQuery.state.pageSize,
            sort: tableQuery.state.sort,
            search: tableQuery.state.search,
            ast: ast?.value,
            refinement: refinement?.value,
        }), load, { deep: true });
    }
    return { items, pageResponse, loading, loadError, loadErrorCode, reload: load };
}
